/**
 * YouTube Hover Preview
 * Creates a simple animated preview by cycling through available YouTube thumbnails
 * @param {HTMLElement} listItem - The list item element containing the YouTube trailer
 * @return {Component}
 */

import Component from "../../lib/Component.js";

const YouTubeHoverPreview = (listItem) => {
  if (!listItem || !listItem.dataset.youtubeId) {
    return Component.noop();
  }

  const videoId = listItem.dataset.youtubeId;
  const picture = listItem.querySelector("picture");
  const originalThumbnails = Array.from(picture.querySelectorAll("img"));

  let images = [];
  let currentIndex = -1;
  let animationInterval = null;
  let hoverTimeout = null;
  let isHovering = false;
  let container = null;
  let previewImgs = [];

  const loadThumbnails = async () => {
    // YouTube provides numbered thumbnails (1.jpg, 2.jpg, 3.jpg)
    // These are video frames at different timestamps
    const thumbnailNumbers = ["1", "2", "3"];
    const loadPromises = [];

    for (const num of thumbnailNumbers) {
      const url = `https://i.ytimg.com/vi/${videoId}/${num}.jpg`;
      const promise = new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
      loadPromises.push(promise);
    }

    const results = await Promise.all(loadPromises);
    images = results.filter((img) => img !== null);

    if (images.length === 0) {
      if (process.env.NODE_ENV === "development") console.warn("No preview thumbnails available for", videoId);
    }
  };

  const createPreviewImages = () => {
    // Create container for all preview images
    container = document.createElement("div");
    container.className = "hover-preview-container";

    // Create img elements for each thumbnail
    previewImgs = images.map((img) => {
      const previewImg = document.createElement("img");
      previewImg.className = "hover-preview";
      previewImg.src = img.src;
      previewImg.style.opacity = "0";
      container.appendChild(previewImg);
      return previewImg;
    });

    picture.appendChild(container);
  };

  const showFrame = (index) => {
    previewImgs.forEach((img, i) => {
      if (i === index) {
        img.style.opacity = "1";
      } else {
        img.style.opacity = "0";
      }
    });
  };

  const stopAnimation = () => {
    if (animationInterval) {
      clearInterval(animationInterval);
      animationInterval = null;
    }

    returnToOriginal();
  };

  const returnToOriginal = () => {
    if (animationInterval) {
      clearInterval(animationInterval);
      animationInterval = null;
    }

    // Fade out all preview images
    if (previewImgs.length > 0) {
      previewImgs.forEach((img) => (img.style.opacity = "0"));
    }

    // Fade in original thumbnails
    originalThumbnails.forEach((img) => (img.style.opacity = "1"));

    // Hide container after transition
    setTimeout(() => {
      if (container && !isHovering) {
        container.style.display = "none";
      }
    }, 500);
  };

  const startAnimation = () => {
    if (images.length === 0) return;

    if (!container) {
      createPreviewImages();
    }

    currentIndex = 0;
    container.style.display = "block";

    // Use requestAnimationFrame to ensure the browser has rendered the initial state
    // before we start the fade transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Now fade out original and fade in first frame simultaneously
        showFrame(0);
        originalThumbnails.forEach((img) => (img.style.opacity = "0"));
      });
    });

    // Continue with remaining frames: 2 -> 3 -> original (stop)
    animationInterval = setInterval(() => {
      currentIndex++;

      if (currentIndex < images.length) {
        // Show preview frames
        showFrame(currentIndex);
      } else {
        // Animation complete, return to original and stop
        stopAnimation();
      }
    }, 1200);
  };

  const onHoverStart = async () => {
    isHovering = true;

    // Add a 300ms delay before starting animation to prevent
    // animations from starting when quickly moving across tiles
    hoverTimeout = setTimeout(async () => {
      if (!isHovering) return;

      if (images.length === 0) {
        await loadThumbnails();
      }

      if (images.length > 0 && isHovering) {
        startAnimation();
      }
    }, 300);
  };

  const onHoverEnd = () => {
    isHovering = false;

    // Cancel the hover timeout if mouse leaves before animation starts
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }

    returnToOriginal();
  };

  const cleanup = () => {
    stopAnimation();
    if (container) {
      container.remove();
    }
  };

  return new Component({
    componentName: "YouTubeHoverPreview",
    onMount() {
      // Only enable on devices with hover capability (desktop)
      if (window.matchMedia("(hover: hover)").matches) {
        listItem.addEventListener("mouseenter", onHoverStart);
        listItem.addEventListener("mouseleave", onHoverEnd);
      }
    },
    onUnmount() {
      listItem.removeEventListener("mouseenter", onHoverStart);
      listItem.removeEventListener("mouseleave", onHoverEnd);
      cleanup();
    },
  });
};

export default YouTubeHoverPreview;
